<template>
  <TransferStepCard :step="2" :title="title" @back="$emit('back')">
    <div class="flex flex-col gap-4">
      <AppInput
        :model-value="search"
        :placeholder="searchPlaceholder"
        @update:model-value="$emit('update:search', String($event ?? ''))"
      >
        <template #icon>
          <i class="pi pi-search" />
        </template>
      </AppInput>

      <div v-if="loading" class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        <div
          v-for="index in 6"
          :key="index"
          class="rounded-2xl border border-[var(--p-surface-200)] dark:border-[var(--p-surface-700)] bg-[var(--p-surface-0)] dark:bg-[var(--p-surface-900)] p-4 animate-pulse"
        >
          <div class="space-y-3">
            <div class="w-12 h-12 rounded-2xl bg-[var(--p-surface-200)] dark:bg-[var(--p-surface-700)]" />
            <div class="h-4 rounded bg-[var(--p-surface-200)] dark:bg-[var(--p-surface-700)]" />
            <div class="h-3 w-2/3 rounded bg-[var(--p-surface-200)] dark:bg-[var(--p-surface-700)]" />
          </div>
        </div>
      </div>

      <div v-else-if="campaigns.length" class="grid max-h-[calc((4rem*4)+(1rem*2))] grid-cols-1 gap-4 overflow-y-auto pr-2 md:grid-cols-2 xl:grid-cols-3 pb-2">
        <button
          v-for="campaign in campaigns"
          :key="campaign.id"
          type="button"
          class="app-card-glass shadow dark:bg-transparent text-left cursor-pointer rounded-2xl border p-4 transition-all duration-200"
          :class="
            modelValue?.id === campaign.id
              ? 'border-[var(--p-primary-color)] bg-[var(--p-primary-color)]/10 shadow-md'
              : 'hover:bg-[var(--p-primary-50)]/50 dark:hover:bg-[var(--p-primary-800)]/10  hover:shadow-md'
          "
          @click="$emit('update:modelValue', campaign)"
        >
          <div class="flex items-start gap-3">
            <div
              class="flex items-center justify-center w-12 h-12 rounded-lg bg-[var(--p-primary-color)]/12 text-[var(--p-primary-color)] flex-shrink-0"
            >
              <img
                v-if="campaign.logo"
                :src="campaign.logo"
                :alt="`Logo de ${campaign.name}`"
                class="h-full w-full rounded-lg object-cover"
              />
              <IconCampaing v-else width="22" height="22" class=" dark:text-[var(--p-primary-100)]"/>
            </div>
            <div class="min-w-0 flex-1">
              <p class="text-sm font-semibold truncate text-[var(--p-primary-color)] dark:text-[var(--p-text-color)]">
                {{ campaign.name }}
              </p>
              <p class="text-xs text-[var(--p-surface-700)] dark:text-[var(--p-surface-100)]">
                {{ getCampaignTypeLabel(campaign.type) }}
              </p>
            </div>
          </div>
        </button>
      </div>

      <div
        v-else
        class="rounded-2xl border border-dashed border-[var(--p-surface-300)] dark:border-[var(--p-surface-700)] p-6 text-center text-sm text-[var(--p-surface-500)] dark:text-[var(--p-surface-400)]"
      >
        {{ emptyText }}
      </div>
    </div>
  </TransferStepCard>
</template>

<script setup lang="ts">
import AppInput from '@/components/atoms/inputs/AppInput.vue'
import TransferStepCard from './TransferStepCard.vue'
import { TypeCampaign } from '@/services/campaign/enums/type-campaign.enum'
import type { CampaignOption } from '@/components/whatsapp/composables/useTransferDialog'
import IconCampaing from '@/assets/svg/campaign_icon.svg?component'

interface Props {
  modelValue: CampaignOption | null
  campaigns: CampaignOption[]
  search: string
  loading: boolean
  title: string
  searchPlaceholder: string
  emptyText: string
}

defineProps<Props>()

defineEmits<{
  'update:modelValue': [value: CampaignOption | null]
  'update:search': [value: string]
  back: []
}>()

const getCampaignTypeLabel = (type?: TypeCampaign): string => {
  if (!type) return ''

  const labels: Record<TypeCampaign, string> = {
    [TypeCampaign.BLASTING]: 'Blasting',
    [TypeCampaign.INCOMING]: 'Entrante',
    [TypeCampaign.OUTGOING]: 'Saliente',
    [TypeCampaign.CHAT]: 'Chat',
    [TypeCampaign.PREVIEW]: 'Preview',
  }

  return labels[type] || type
}
</script>
